import { ReactNode, useMemo, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { ContractorContext, ContractorContextType } from "./ContractorContext";
import { fetchContractor } from "@/services/contractors";
import { Module } from "@/constants";
import {
  useContractorHandlers,
  useContractorLoading,
  useContractorError,
} from "../hooks";
import { useContractorDialog } from "../hooks/useContractorDialog";
import { ContractorConfirmDialog } from "../components/ContractorConfirmDialog";

interface ContractorProviderProps {
  children: ReactNode;
  contractorId: string;
}

export const ContractorProvider = ({
  children,
  contractorId,
}: ContractorProviderProps) => {
  const query = useQuery({
    queryKey: [Module.Contractors, contractorId],
    queryFn: () => fetchContractor(contractorId),
    enabled: !!contractorId,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const { data } = query;

  const { dialogHandlerKey, openDialog, closeDialog, confirmDialog } =
    useContractorDialog();

  const {
    handleActivate,
    handleDeactivate,
    handleUpdate,
    handleDelete,
    mutations,
  } = useContractorHandlers({
    contractorId,
    openDialog,
    closeDialog,
  });

  const queries = { contractorQuery: query };

  const { errorState, clearError } = useContractorError(queries, mutations);

  const loadingState = useContractorLoading(queries, mutations);

  const refreshContractor = useCallback(() => {
    query.refetch();
  }, [query]);

  const contextValue: ContractorContextType = useMemo(
    () => ({
      query,
      data,
      errorState,
      loadingState,

      handleActivate,
      handleDeactivate,
      handleUpdate,
      handleDelete,
      clearError,
      refreshContractor,
    }),
    [
      query,
      data,
      errorState,
      loadingState,
      handleActivate,
      handleDeactivate,
      handleUpdate,
      handleDelete,
      clearError,
      refreshContractor,
    ],
  );

  return (
    <ContractorContext.Provider value={contextValue}>
      {children}
      <ContractorConfirmDialog
        handlerKey={dialogHandlerKey}
        onConfirm={confirmDialog}
        onCancel={closeDialog}
      />
    </ContractorContext.Provider>
  );
};
